import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable()
export class HttpRequestDataInterceptor implements HttpInterceptor {
  private _delimiterPrefix = '__ERMDelimiter__';
  private _delimiterSuffix = '__ERMDelimiter__';

  private readonly _regexAndOr = new RegExp(
    [
      '\\b(?:' +
        [
          // DDL (Data Definition Language)
          'create',
          'alter',
          'drop',
          'truncate',
          'rename',
          'database',
          'schema',
          'table',
          'view',
          'index',
          'sequence',
          'trigger',
          'procedure',
          'function',

          // DML (Data Manipulation Language)
          'select',
          'insert',
          'update',
          'delete',
          'merge',
          'upsert',

          // DQL (Data Query Language)
          'from',
          'where',
          'group by',
          'having',
          'order by',
          'limit',
          'offset',
          'fetch',
          'top',
          'distinct',
          'all',
          'any',
          'some',
          'exists',

          // Joins
          'join',
          'inner join',
          'left join',
          'right join',
          'full join',
          'cross join',
          'outer join',
          'natural join',

          // Set Operations
          'union',
          'union all',
          'intersect',
          'except',
          'minus',

          // Constraints
          'primary key',
          'foreign key',
          'unique',
          'not null',
          'check',
          'default',
          'constraint',
          'references',
          'on delete',
          'on update',
          'cascade',

          // Logical Operators
          'and',
          'or',
          'not',
          'in',
          'between',
          'like',
          'is',
          'is null',
          'is not null',

          // Functions
          'cast',
          'convert',
          'concat',
          'substring',
          'length',
          'upper',
          'lower',
          'count',
          'sum',
          'avg',
          'min',
          'max',
          'round',
          'floor',
          'ceil',
          'date',
          'datetime',
          'timestamp',
          'now',
          'current_date',
          'current_timestamp',

          // Control Flow
          'case',
          'when',
          'then',
          'else',
          'end',
          'if',
          'ifnull',
          'nullif',
          'coalesce',

          // Transaction Control
          'begin',
          'commit',
          'rollback',
          'savepoint',
          'transaction',

          // Execution
          'exec',
          'execute',
          'call',
          'declare',
          'set',

          // Advanced Features
          'with',
          'as',
          'recursive',
          'partition by',
          'over',
          'row_number',
          'rank',
          'dense_rank',
          'lag',
          'lead',
          'window',

          // Data Types
          'int',
          'integer',
          'bigint',
          'smallint',
          'decimal',
          'numeric',
          'float',
          'real',
          'char',
          'varchar',
          'text',
          'blob',
          'clob',
          'binary',
          'varbinary',
          'date',
          'time',
          'datetime',
          'timestamp',
          'interval',
          'boolean',
          'json',

          // Access Control
          'grant',
          'revoke',
          'deny',
          'role',
          'user',
          'privileges',

          // Database-Specific (add more as needed)
          'show',
          'describe',
          'explain',
          'analyze',
          'vacuum',
          'reindex',
          'use',
          'backup',
          'restore',
          'import',
          'export',
          'zip',
          'entity',
          'entities',
        ]
          .map((keyword) => keyword.replace(/\s+/g, '\\s+'))
          .join('|') +
        ')\\b',

      // Additional complex patterns for SQLi detection (unchanged)
      '(?:\\bhaving\\b\\s?(?:[\'"][^=]{1,10}[\'"]\\s?[=<>]+|\\d{1,10}\\s?[=<>]+))',
      "\\bhaving\\b\\s+(?:'[^=]{1,10}'|\\d{1,10})\\s*?[=<>]",
      'exists\\s(?:s(?:elect\\S(?:if(?:null)?\\s\\(|concat|top)|ystem\\s\\()|' +
        "\\bhaving\\b\\s+\\d{1,10}|'[^=]{1,10}'|\\sselect)",
      '\\bexecute\\s{1,5}[\\w\\.$]{1,5}\\s{0,3}',
    ].join('|'),
    'gi',
  );

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    /**
     * 'jemma is part of "clinsphere" and doesn't have the same deprecated firewall'
     * 'Hence we do not have to apply delimiter transformation for jemma API calls'
     **/
    if (req.url.toLowerCase().includes('jemma')) {
      return next.handle(req);
    }

    let modifiedReq = req;
    if (req.params && req.params.keys().length > 0) {
      const newParams = this._processQueryParams(req.params);
      modifiedReq = modifiedReq.clone({ params: newParams });
    }

    if (['POST', 'PUT', 'PATCH'].includes(req.method) && req.body) {
      const newBody =
        req.body instanceof FormData ? req.body : this._processBody(req.body);
      modifiedReq = modifiedReq.clone({ body: newBody });
    }

    return next.handle(modifiedReq);
  }

  private _transformWord(match: string): string {
    const transformed = match.split('').join('_');
    return `${this._delimiterPrefix}${transformed}${this._delimiterSuffix}`;
  }

  private _processQueryParams(params: HttpParams): HttpParams {
    let modifiedParams = params;
    params.keys().forEach((key) => {
      const value = params.get(key);
      if (value && this._regexAndOr.test(value)) {
        const newValue = value.replace(
          this._regexAndOr,
          this._transformWord.bind(this),
        );
        modifiedParams = modifiedParams.set(key, newValue);
      }
    });
    return modifiedParams;
  }

  private _processBody(body: unknown): unknown | Record<string, unknown> {
    if (typeof body === 'string') {
      return body.replace(this._regexAndOr, this._transformWord.bind(this));
    }
    if (Array.isArray(body)) {
      return body.map((item) => this._processBody(item));
    }
    if (body !== null && typeof body === 'object') {
      const newBody: Record<string, unknown> = {};
      Object.keys(body).forEach((key) => {
        newBody[key] = this._processBody(body[key as keyof typeof body]);
      });
      return newBody;
    }
    return body;
  }
}
